import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash, LockKey, Warning, Buildings, EnvelopeSimple, PaperPlaneTilt, Gear, ShieldCheck, Clock, Users, Key, Copy, TelegramLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { SessieOverzicht } from "@/components/SessieOverzicht";
import { useAuth, usePermissions } from "@/App";
import { LoonSlot } from "@/components/LoonSlot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAGEN = [
  { k: "ma", l: "Maandag" },
  { k: "di", l: "Dinsdag" },
  { k: "wo", l: "Woensdag" },
  { k: "do", l: "Donderdag" },
  { k: "vr", l: "Vrijdag" },
  { k: "za", l: "Zaterdag" },
  { k: "zo", l: "Zondag" },
];

const MAAND_NAMEN = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December"
];

export default function Instellingen() {
  const now = new Date();
  const auth = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(auth?.rol) || auth?.role === "admin";
  // Rol Machtigingen is enkel voor de superadmin — admins zien de tab niet
  const isSuperadmin = auth?.rol === "superadmin";
  const alleRechten = usePermissions();
  const magTab = (perm) => isSuperadmin || alleRechten?.[auth?.role]?.[perm] === true;
  const zichtbareTabs = [
    "machtigingen",
    ...(magTab("instellingen_email") ? ["email"] : []),
    ...(magTab("instellingen_beheer") ? ["beheer"] : []),
    ...(magTab("instellingen_beveiliging") ? ["beveiliging"] : []),
  ];
  const tabColsClass = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" }[zichtbareTabs.length];

  // Delete month
  const [deleteMaand, setDeleteMaand] = useState(String(now.getMonth() + 1));
  const [deleteJaar, setDeleteJaar] = useState(String(now.getFullYear()));
  const [deleteWachtwoord, setDeleteWachtwoord] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // Password
  const [huidigWw, setHuidigWw] = useState("");
  const [nieuwWw, setNieuwWw] = useState("");
  const [changingWw, setChangingWw] = useState(false);

  // Filiaal
  const [filialen, setFilialen] = useState([]);
  const [selectedFiliaal, setSelectedFiliaal] = useState("");
  const [filiaalWachtwoord, setFiliaalWachtwoord] = useState("");
  const [deletingFiliaal, setDeletingFiliaal] = useState(false);
  const [filiaalConfirmOpen, setFiliaalConfirmOpen] = useState(false);

  // Medewerker
  const [medewerkers, setMedewerkers] = useState([]);
  const [selectedMedewerker, setSelectedMedewerker] = useState("");
  const [medWachtwoord, setMedWachtwoord] = useState("");
  const [deletingMed, setDeletingMed] = useState(false);
  const [medConfirmOpen, setMedConfirmOpen] = useState(false);

  // Aanwezigheid log
  const [aanwLogWw, setAanwLogWw] = useState("");
  const [deletingAanw, setDeletingAanw] = useState(false);
  const [aanwConfirmOpen, setAanwConfirmOpen] = useState(false);
  const [aanwResult, setAanwResult] = useState(null);

  // Sessie / auto-logout
  const [autoLogoutAan, setAutoLogoutAan] = useState(true);
  const [autoLogoutMin, setAutoLogoutMin] = useState("30");
  const [loonMin, setLoonMin] = useState("30");
  const [savingSessie, setSavingSessie] = useState(false);

  // Email
  const [adminEmail, setAdminEmail] = useState("");
  const [rapportUur, setRapportUur] = useState("18");
  const [teLaatEmail, setTeLaatEmail] = useState(true);
  const [teLaatVanaf, setTeLaatVanaf] = useState("10:00");
  const [uitklokHerinnering, setUitklokHerinnering] = useState(true);
  const [uitklokMin, setUitklokMin] = useState("30");
  const [herinneringBezig, setHerinneringBezig] = useState(false);
  const [werkdagStart, setWerkdagStart] = useState("");
  const [werkdagEind, setWerkdagEind] = useState("18:00");
  const [weekrooster, setWeekrooster] = useState({});
  const kopieerDagNaarAlle = (k, alleenWerkdagen = false) => {
    setWeekrooster((w) => {
      const rij = w[k] || {};
      const doel = alleenWerkdagen ? ["ma", "di", "wo", "do", "vr"] : DAGEN.map((d) => d.k);
      const nieuw = { ...w };
      doel.forEach((dk) => (nieuw[dk] = { ...rij }));
      return nieuw;
    });
    toast.success(alleenWerkdagen ? "Gekopieerd naar ma–vr" : "Gekopieerd naar alle dagen");
  };
  const [startTolerantie, setStartTolerantie] = useState("30");
  const [senderEmail, setSenderEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingDag, setSendingDag] = useState(false);

  // Telegram
  const [tg, setTg] = useState({
    bot_token: "", chat_id: "", actief: false, te_laat: true, uitklok: true, dagrapport: true, ritten: true, rit_open: true, rit_open_uren: 8, blokkade: true, nieuwe_medewerker: true,
  });
  const [tgSaving, setTgSaving] = useState(false);
  const [tgTesten, setTgTesten] = useState(false);
  const [tgZoeken, setTgZoeken] = useState(false);

  const setTgVeld = (k, v) => setTg((p) => ({ ...p, [k]: v }));

  const handleSaveTelegram = async () => {
    setTgSaving(true);
    try {
      await axios.put(`${API}/instellingen/telegram`, tg);
      toast.success("Telegram instellingen opgeslagen");
    } catch (e) { toast.error(e.response?.data?.detail || "Fout bij opslaan"); }
    finally { setTgSaving(false); }
  };

  const handleTelegramChatId = async () => {
    setTgZoeken(true);
    try {
      const { data } = await axios.post(`${API}/instellingen/telegram/chat-id`, { bot_token: tg.bot_token });
      setTgVeld("chat_id", data.chat_id);
      toast.success(`Chat gevonden: ${data.naam || data.chat_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Chat niet gevonden"); }
    finally { setTgZoeken(false); }
  };

  const handleTelegramTest = async () => {
    setTgTesten(true);
    try {
      // Eerst bewaren, anders test je waarden die nog niet opgeslagen zijn
      await axios.put(`${API}/instellingen/telegram`, tg);
      await axios.post(`${API}/instellingen/telegram/test`, {
        bot_token: tg.bot_token, chat_id: tg.chat_id,
      });
      toast.success("Opgeslagen én testbericht verstuurd naar Telegram");
    } catch (e) { toast.error(e.response?.data?.detail || "Versturen mislukt"); }
    finally { setTgTesten(false); }
  };

  const [tgHerinnering, setTgHerinnering] = useState(false);
  const handleTestHerinnering = async () => {
    setTgHerinnering(true);
    try {
      const { data } = await axios.post(`${API}/aanwezigheid/herinnering-nu`);
      if (data.aantal) toast.success(`Melding verstuurd: ${data.aantal} medewerker(s) nog ingeklokt`);
      else toast.info("Niemand staat nog ingeklokt — er valt nu niets te melden");
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setTgHerinnering(false); }
  };

  // Resend Domains
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [newDomainName, setNewDomainName] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [verifyingDomain, setVerifyingDomain] = useState(null);
  const [domainDetails, setDomainDetails] = useState({}); // {id: {records: [], status}}

  const loadDomains = async () => {
    setLoadingDomains(true);
    try {
      const r = await axios.get(`${API}/instellingen/domains`);
      setDomains(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon domeinen niet ophalen");
    } finally {
      setLoadingDomains(false);
    }
  };

  const fetchDomainDetails = async (id) => {
    try {
      const r = await axios.get(`${API}/instellingen/domains/${id}`);
      setDomainDetails((prev) => ({ ...prev, [id]: r.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon records niet ophalen");
    }
  };

  const toggleExpand = (id) => {
    if (expandedDomain === id) {
      setExpandedDomain(null);
    } else {
      setExpandedDomain(id);
      if (!domainDetails[id]) fetchDomainDetails(id);
    }
  };

  const handleAddDomain = async () => {
    const name = newDomainName.trim().toLowerCase();
    if (!name || !name.includes(".")) {
      toast.error("Geef een geldig domein (bv. gontex.be)");
      return;
    }
    setAddingDomain(true);
    try {
      const r = await axios.post(`${API}/instellingen/domains`, { name });
      toast.success(`Domein ${name} aangemaakt — voeg DNS records toe en klik Verifieer`);
      setNewDomainName("");
      setDomainDetails((prev) => ({ ...prev, [r.data.id]: r.data }));
      setExpandedDomain(r.data.id);
      await loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toevoegen mislukt");
    } finally {
      setAddingDomain(false);
    }
  };

  const handleVerifyDomain = async (id) => {
    setVerifyingDomain(id);
    try {
      const r = await axios.post(`${API}/instellingen/domains/${id}/verify`);
      const status = r.data?.status || "pending";
      if (status === "verified") toast.success("Domein is geverifieerd!");
      else toast.message(`Status: ${status} — DNS kan tot 48u duren`);
      setDomainDetails((prev) => ({ ...prev, [id]: r.data }));
      await loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verificatie mislukt");
    } finally {
      setVerifyingDomain(null);
    }
  };

  const handleDeleteDomain = async (id, name) => {
    if (!window.confirm(`Domein ${name} verwijderen uit Resend?`)) return;
    try {
      await axios.delete(`${API}/instellingen/domains/${id}`);
      toast.success("Domein verwijderd");
      if (expandedDomain === id) setExpandedDomain(null);
      await loadDomains();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const copyToClipboard = async (text, label = "Gekopieerd") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  // Machtigingen
  const [machtigingen, setMachtigingen] = useState(null);
  const [savingPerms, setSavingPerms] = useState(false);

  const PERM_LABELS = {
    dashboard: "Dashboard",
    medewerkers: "Medewerkers (bekijken)",
    medewerkers_bewerken: "Medewerkers (bewerken)",
    klokken_anderen: "Anderen in/uitklokken",
    filialen: "Filialen (bekijken)",
    filialen_bewerken: "Filialen (bewerken)",
    aanwezigheid: "Aanwezigheid",
    weekoverzicht: "Weekoverzicht",
    maandoverzicht: "Maandoverzicht",
    uren_invullen: "Uren invullen in maandoverzicht-grid",
    kosten: "Kosten",
    wagenpark: "Wagenpark",
    kortingscalculator: "Kortingscalculator",
    stock_bijvullen: "Stock Bijvullen",
    kassa: "Kassa / Verkocht (bekijken + uit stock halen)",
    kassa_import: "Kassa Excel importeren",
    ctn: "Bestelling CTN (bekijken)",
    ctn_beheren: "Bestelling CTN (bestellingen, leveranciers en categorieën beheren)",
    ctn_aankoop: "CTN aankoopprijzen zien (toewijzing door superadmin)",
    ctn_verkoop: "CTN verkoopprijzen zien (toewijzing door superadmin)",
    bestanden: "Bestanden bekijken/downloaden",
    bestanden_beheren: "Bestanden uploaden/verwijderen",
    lonen_zien: "Uurlonen & loonkosten zien",
    verlof: "Verlof",
    planning: "Planning",
    instellingen: "Instellingen",
    instellingen_email: "Instellingen → Email",
    instellingen_beheer: "Instellingen → Beheer",
    instellingen_beveiliging: "Instellingen → Beveiliging",
    firmas: "Firma's (eigen firma + gebruikers beheren)",
  };
  const ROLLEN = ["admin", "manager", "medewerker", "verkoper"];
  const PERM_KEYS = Object.keys(PERM_LABELS);
  const [verborgenRechten, setVerborgenRechten] = useState([]);
  const [loonOpen, setLoonOpen] = useState(false);
  // Admins zien de verborgen rechten niet; de superadmin ziet alles met een oog-schakelaar
  const zichtbarePerms = isSuperadmin ? PERM_KEYS : PERM_KEYS.filter((p) => !verborgenRechten.includes(p));

  useEffect(() => {
    axios.get(`${API}/filialen`).then((r) => setFilialen(r.data)).catch(() => {});
    axios.get(`${API}/medewerkers`).then((r) => setMedewerkers(r.data)).catch(() => {});
    axios.get(`${API}/instellingen/machtigingen`).then((r) => setMachtigingen(r.data)).catch(() => {});
    axios.get(`${API}/instellingen/machtigingen-verborgen`).then((r) => setVerborgenRechten(r.data.rechten || [])).catch(() => {});
    axios.get(`${API}/auth/loon-status`).then((r) => setLoonOpen(r.data.ontgrendeld === true)).catch(() => {});
    axios.get(`${API}/instellingen/email`).then((r) => {
      setAdminEmail(r.data.admin_email || "");
      setRapportUur(String(r.data.rapport_uur || 18));
      setTeLaatEmail(r.data.te_laat_email !== false);
      setTeLaatVanaf(r.data.te_laat_vanaf || "10:00");
      setWerkdagStart(r.data.werkdag_start || "");
      setWerkdagEind(r.data.werkdag_eind || "18:00");
      setWeekrooster(r.data.weekrooster || {});
      setStartTolerantie(String(r.data.start_tolerantie_min ?? 30));
      setUitklokHerinnering(r.data.uitklok_herinnering !== false);
      setUitklokMin(String(r.data.uitklok_herinnering_min ?? 30));
      setSenderEmail(r.data.sender_email || "");
    }).catch(() => {});
    axios.get(`${API}/instellingen/sessie`).then((r) => {
      setAutoLogoutAan(r.data.auto_logout_actief !== false);
      setAutoLogoutMin(String(r.data.auto_logout_minuten || 30));
      setLoonMin(String(r.data.loon_minuten || 30));
    }).catch(() => {});
    axios.get(`${API}/instellingen/telegram`).then((r) => {
      setTg({
        bot_token: r.data.bot_token || "",
        chat_id: r.data.chat_id || "",
        actief: !!r.data.actief,
        te_laat: r.data.te_laat !== false,
        uitklok: r.data.uitklok !== false,
        dagrapport: r.data.dagrapport !== false,
        ritten: r.data.ritten !== false,
        rit_open: r.data.rit_open !== false,
        blokkade: r.data.blokkade !== false,
        nieuwe_medewerker: r.data.nieuwe_medewerker !== false,
        rit_open_uren: r.data.rit_open_uren || 8,
      });
    }).catch(() => {});
    loadDomains();
  }, []);

  const togglePerm = (rol, perm) => {
    if (rol === "admin" && perm === "instellingen") return; // Admin always has instellingen
    setMachtigingen((prev) => ({
      ...prev,
      [rol]: { ...prev[rol], [perm]: !prev[rol]?.[perm] },
    }));
  };

  const toggleVerborgen = (perm) => {
    setVerborgenRechten((prev) => (prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]));
  };

  const handleSavePerms = async () => {
    setSavingPerms(true);
    try {
      await axios.put(`${API}/instellingen/machtigingen`, machtigingen);
      if (isSuperadmin) {
        await axios.put(`${API}/instellingen/machtigingen-verborgen`, { rechten: verborgenRechten });
      }
      toast.success("Machtigingen opgeslagen");
    } catch (e) { toast.error("Fout bij opslaan"); }
    finally { setSavingPerms(false); }
  };

  const handleSaveSessie = async () => {
    const minuten = parseInt(autoLogoutMin, 10);
    if (autoLogoutAan && (!minuten || minuten < 1 || minuten > 480)) {
      toast.error("Kies tussen 1 en 480 minuten");
      return;
    }
    setSavingSessie(true);
    try {
      await axios.put(`${API}/instellingen/sessie`, {
        auto_logout_actief: autoLogoutAan,
        auto_logout_minuten: minuten || 30,
        loon_minuten: parseInt(loonMin, 10) || 30,
      });
      window.dispatchEvent(new Event("sessie-instellingen-gewijzigd"));
      toast.success(autoLogoutAan ? `Automatisch uitloggen na ${minuten} min` : "Automatisch uitloggen uitgezet");
    } catch (e) { toast.error(e.response?.data?.detail || "Fout bij opslaan"); }
    finally { setSavingSessie(false); }
  };

  // Handlers
  const handleSaveEmail = async () => {
    setSavingEmail(true);
    try {
      await axios.put(`${API}/instellingen/email`, { admin_email: adminEmail, rapport_uur: parseInt(rapportUur), te_laat_email: teLaatEmail, sender_email: senderEmail, te_laat_vanaf: teLaatVanaf, werkdag_start: werkdagStart, werkdag_eind: werkdagEind, weekrooster, start_tolerantie_min: parseInt(startTolerantie || "0"), uitklok_herinnering: uitklokHerinnering, uitklok_herinnering_min: parseInt(uitklokMin || "30") });
      toast.success("Email instellingen opgeslagen");
    } catch (e) { toast.error("Fout bij opslaan"); }
    finally { setSavingEmail(false); }
  };

  const handleTestEmail = async () => {
    if (!adminEmail) { toast.error("Vul eerst een admin email in"); return; }
    setSendingTest(true);
    try {
      await axios.post(`${API}/email/test`, { email: adminEmail });
      toast.success(`Test email verstuurd naar ${adminEmail}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fout bij versturen"); }
    finally { setSendingTest(false); }
  };

  const handleSendDagrapport = async () => {
    if (!adminEmail) { toast.error("Vul eerst een admin email in"); return; }
    setSendingDag(true);
    try {
      await axios.post(`${API}/email/dagrapport`, { email: adminEmail });
      toast.success(`Dagrapport verstuurd naar ${adminEmail}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Fout bij versturen"); }
    finally { setSendingDag(false); }
  };

  const handleDeleteConfirm = () => {
    if (!deleteWachtwoord) { toast.error("Vul het admin wachtwoord in"); return; }
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/admin/verwijder-maand`, { jaar: parseInt(deleteJaar), maand: parseInt(deleteMaand), wachtwoord: deleteWachtwoord });
      setDeleteResult(res.data);
      toast.success(`Data verwijderd voor ${MAAND_NAMEN[parseInt(deleteMaand) - 1]} ${deleteJaar}`);
      setConfirmOpen(false); setDeleteWachtwoord("");
    } catch (e) { toast.error(e.response?.data?.detail || "Fout bij verwijderen"); }
    finally { setDeleting(false); }
  };

  const [nieuwLoginEmail, setNieuwLoginEmail] = useState("");
  const [emailWw, setEmailWw] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);

  const handleChangeEmail = async () => {
    if (!nieuwLoginEmail.trim()) { toast.error("Vul een nieuw e-mailadres in"); return; }
    if (!emailWw) { toast.error("Vul je huidige wachtwoord in ter bevestiging"); return; }
    setChangingEmail(true);
    try {
      const res = await axios.post(`${API}/auth/email`, {
        nieuw_email: nieuwLoginEmail.trim(),
        wachtwoord: emailWw,
      });
      if (res.data?.access_token) localStorage.setItem("hr_token", res.data.access_token);
      toast.success("E-mailadres gewijzigd — gebruik dit adres bij je volgende aanmelding");
      setNieuwLoginEmail("");
      setEmailWw("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wijzigen mislukt");
    } finally { setChangingEmail(false); }
  };

  const handleChangePassword = async () => {
    if (!huidigWw || !nieuwWw) { toast.error("Vul beide velden in"); return; }
    if (nieuwWw.length < 4) { toast.error("Min. 4 tekens"); return; }
    setChangingWw(true);
    try {
      await axios.post(`${API}/admin/wijzig-wachtwoord`, { huidig_wachtwoord: huidigWw, nieuw_wachtwoord: nieuwWw });
      toast.success("Wachtwoord gewijzigd"); setHuidigWw(""); setNieuwWw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setChangingWw(false); }
  };

  const handleFiliaalDeleteConfirm = () => {
    if (!selectedFiliaal) { toast.error("Selecteer een filiaal"); return; }
    if (!filiaalWachtwoord) { toast.error("Vul het admin wachtwoord in"); return; }
    setFiliaalConfirmOpen(true);
  };

  const handleFiliaalDelete = async () => {
    setDeletingFiliaal(true);
    try {
      const res = await axios.post(`${API}/admin/verwijder-filiaal`, { filiaal_id: selectedFiliaal, wachtwoord: filiaalWachtwoord });
      toast.success(res.data.message); setFiliaalConfirmOpen(false); setSelectedFiliaal(""); setFiliaalWachtwoord("");
      const filRes = await axios.get(`${API}/filialen`); setFilialen(filRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setDeletingFiliaal(false); }
  };

  const selectedFilNaam = filialen.find((f) => f.id === selectedFiliaal)?.naam || "";
  const selectedMedNaam = (() => {
    const m = medewerkers.find((m) => m.id === selectedMedewerker);
    return m ? `${m.voornaam} ${m.achternaam}` : "";
  })();

  const handleMedDeleteConfirm = () => {
    if (!selectedMedewerker) { toast.error("Selecteer een medewerker"); return; }
    if (!medWachtwoord) { toast.error("Vul het admin wachtwoord in"); return; }
    setMedConfirmOpen(true);
  };

  const handleMedDelete = async () => {
    setDeletingMed(true);
    try {
      const res = await axios.post(`${API}/admin/verwijder-medewerker`, { medewerker_id: selectedMedewerker, wachtwoord: medWachtwoord });
      toast.success(res.data.message); setMedConfirmOpen(false); setSelectedMedewerker(""); setMedWachtwoord("");
      const medRes = await axios.get(`${API}/medewerkers`); setMedewerkers(medRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setDeletingMed(false); }
  };

  const handleAanwDeleteConfirm = () => {
    if (!aanwLogWw) { toast.error("Vul het admin wachtwoord in"); return; }
    setAanwConfirmOpen(true);
  };

  const handleAanwDelete = async () => {
    setDeletingAanw(true);
    try {
      const res = await axios.post(`${API}/admin/verwijder-aanwezigheid`, { wachtwoord: aanwLogWw });
      setAanwResult(res.data);
      toast.success("Aanwezigheidslog verwijderd");
      setAanwConfirmOpen(false); setAanwLogWw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
    finally { setDeletingAanw(false); }
  };

  return (
    <div data-testid="instellingen-page" className="space-y-6">
      <div>
        <h1 className="page-title">Instellingen</h1>
        <p className="text-sm text-muted-foreground mt-1">Beheer en administratie</p>
      </div>

      <Tabs defaultValue={zichtbareTabs[0]} className="w-full max-w-3xl">
        <TabsList className={`rounded-sm w-full grid grid-cols-2 ${tabColsClass} h-auto gap-1 p-1`} data-testid="settings-tabs">
          <TabsTrigger value="machtigingen" className="rounded-sm text-xs font-semibold" data-testid="tab-machtigingen">
            <Key size={15} className="mr-1.5" /> Machtigingen
          </TabsTrigger>
          {magTab("instellingen_email") && (
            <TabsTrigger value="email" className="rounded-sm text-xs font-semibold" data-testid="tab-email">
              <EnvelopeSimple size={15} className="mr-1.5" /> Email
            </TabsTrigger>
          )}
          {magTab("instellingen_beheer") && (
            <TabsTrigger value="beheer" className="rounded-sm text-xs font-semibold" data-testid="tab-beheer">
              <Gear size={15} className="mr-1.5" /> Beheer
            </TabsTrigger>
          )}
          {magTab("instellingen_beveiliging") && (
            <TabsTrigger value="beveiliging" className="rounded-sm text-xs font-semibold" data-testid="tab-beveiliging">
              <ShieldCheck size={15} className="mr-1.5" /> Beveiliging
            </TabsTrigger>
          )}
        </TabsList>

        {/* ═══ MACHTIGINGEN TAB — Admin-kolom alleen-lezen buiten de superadmin ═══ */}
        <TabsContent value="machtigingen" className="mt-5">
          <div className="bg-white border border-border rounded-sm p-6" data-testid="machtigingen-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Rol Machtigingen</h3>
              </div>
              <Button onClick={handleSavePerms} disabled={savingPerms} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-perms-btn">
                {savingPerms ? "Opslaan..." : "Opslaan"}
              </Button>
            </div>
            {machtigingen ? (
              <div className="overflow-x-auto">
                <p className="md:hidden text-[10px] text-muted-foreground mb-1 font-semibold">↔ Veeg zijwaarts voor alle rollen</p>
                <table className="w-full text-sm border-collapse min-w-[520px]">
                  <thead>
                    <tr className="bg-[#E0FFFF]">
                      <th className="px-3 py-2 text-left font-bold border border-[#999] min-w-[180px]">Pagina / Recht</th>
                      {ROLLEN.map((rol) => (
                        <th key={rol} className="px-3 py-2 text-center font-bold border border-[#999] capitalize min-w-[100px]">{rol}</th>
                      ))}
                      {isSuperadmin && (
                        <th className="px-3 py-2 text-center font-bold border border-[#999] min-w-[120px]">Verbergen voor admin</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {zichtbarePerms.map((perm) => {
                      const verborgen = verborgenRechten.includes(perm);
                      return (
                      <tr key={perm} className={`hover:bg-muted/20 ${verborgen ? "bg-[#FFF4E5]" : ""}`}>
                        <td className="px-3 py-2 border border-[#999] font-medium text-xs">
                          {PERM_LABELS[perm]}
                          {perm === "lonen_zien" && (
                            <div className="mt-1.5" data-testid="lonen-zien-slot">
                              <LoonSlot />
                              <span className="block text-[11px] text-muted-foreground mt-1">
                                {loonOpen
                                  ? "Zichtbaar tot de tijd op de knop — daarna weer op slot"
                                  : "Staat na elke aanmelding op slot — geef je wachtwoord om te tonen"}
                              </span>
                            </div>
                          )}
                        </td>
                        {ROLLEN.map((rol) => {
                          const loonSlot = perm === "lonen_zien" && !loonOpen;
                          const locked = (rol === "admin" && perm === "instellingen") || loonSlot || (!isSuperadmin && ["ctn_aankoop", "ctn_verkoop"].includes(perm));
                          const checked = !loonSlot && machtigingen[rol]?.[perm] === true;
                          return (
                            <td key={rol} className="px-3 py-2 border border-[#999] text-center">
                              <Switch
                                checked={checked}
                                onCheckedChange={() => togglePerm(rol, perm)}
                                disabled={locked}
                                data-testid={`perm-${rol}-${perm}`}
                              />
                            </td>
                          );
                        })}
                        {isSuperadmin && (
                          <td className="px-3 py-2 border border-[#999] text-center">
                            <Switch
                              checked={verborgen}
                              onCheckedChange={() => toggleVerborgen(perm)}
                              data-testid={`verberg-${perm}`}
                            />
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="h-48 bg-muted animate-pulse rounded-sm" />
            )}
          </div>
        </TabsContent>

        {/* ═══ EMAIL TAB ═══ */}
        <TabsContent value="email" className="mt-5 space-y-5">
          <div className="bg-white border border-border rounded-sm p-6" data-testid="email-settings-section">
            <h3 className="font-bold text-sm mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Admin Email Adres
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">Admin Email (ontvanger)</Label>
                <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@bedrijf.nl" className="rounded-sm mt-1" data-testid="admin-email-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Rapporten en te laat meldingen worden naar dit adres gestuurd</p>
              </div>

              <div>
                <Label className="text-xs font-semibold">Afzender Email (Resend)</Label>
                <Input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder="onboarding@resend.dev (standaard)" className="rounded-sm mt-1" data-testid="sender-email-input" />
                {(() => {
                  if (!senderEmail) {
                    return (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Leeg = gebruik standaard <code>onboarding@resend.dev</code>. Mails komen dan enkel aan op je Resend-accountadres.
                      </p>
                    );
                  }
                  const senderDomain = senderEmail.split("@")[1]?.toLowerCase();
                  if (!senderDomain) {
                    return <p className="text-[10px] text-red-600 mt-1 font-semibold">Ongeldig e-mailadres (geen @domein)</p>;
                  }
                  const verifiedDomains = domains.filter((d) => d.status === "verified").map((d) => d.name.toLowerCase());
                  const allResendDomains = domains.map((d) => d.name.toLowerCase());
                  if (senderDomain === "resend.dev") {
                    return <p className="text-[10px] text-green-700 mt-1 font-semibold">✓ Resend standaard-afzender</p>;
                  }
                  if (verifiedDomains.includes(senderDomain)) {
                    return <p className="text-[10px] text-green-700 mt-1 font-semibold">✓ Domein {senderDomain} is geverifieerd</p>;
                  }
                  if (allResendDomains.includes(senderDomain)) {
                    return <p className="text-[10px] text-amber-700 mt-1 font-semibold">⚠ Domein {senderDomain} bestaat in Resend maar is nog niet geverifieerd — mails zullen falen</p>;
                  }
                  return <p className="text-[10px] text-red-600 mt-1 font-semibold">✗ Domein {senderDomain} staat niet in je Resend domeinen-lijst → voeg het eerst toe hieronder en verifieer het, anders falen alle mails</p>;
                })()}
              </div>

              <Separator />

              {/* ══ DOMAIN VERIFICATION ══ */}
              <div data-testid="domain-section">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-[#002FA7]" />
                    Resend Domein Verificatie
                  </Label>
                  <button
                    type="button"
                    onClick={loadDomains}
                    disabled={loadingDomains}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline"
                    data-testid="refresh-domains-btn"
                  >
                    {loadingDomains ? "Laden..." : "Ververs"}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground mb-3">
                  Voeg je eigen domein toe, kopieer de DNS-records naar je provider (Combell, Cloudflare, Namecheap...), wacht tot ze actief zijn en klik op <b>Verifieer</b>.
                </p>

                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="gontex.be"
                    value={newDomainName}
                    onChange={(e) => setNewDomainName(e.target.value)}
                    className="rounded-sm flex-1"
                    data-testid="new-domain-input"
                  />
                  <Button
                    onClick={handleAddDomain}
                    disabled={addingDomain}
                    className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
                    data-testid="add-domain-btn"
                  >
                    {addingDomain ? "..." : "Domein toevoegen"}
                  </Button>
                </div>

                {domains.length === 0 ? (
                  <div className="text-center py-6 text-xs text-muted-foreground border border-dashed border-border rounded-sm" data-testid="no-domains">
                    Nog geen domeinen geconfigureerd
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="domains-list">
                    {domains.map((d) => {
                      const isOpen = expandedDomain === d.id;
                      const details = domainDetails[d.id];
                      const records = details?.records || d.records || [];
                      const status = details?.status || d.status;
                      const statusColor = status === "verified" ? "bg-green-100 text-green-800 border-green-300"
                        : status === "failed" ? "bg-red-100 text-red-800 border-red-300"
                        : "bg-amber-100 text-amber-800 border-amber-300";
                      return (
                        <div key={d.id} className="border border-border rounded-sm overflow-hidden" data-testid={`domain-${d.id}`}>
                          <div className="flex items-center justify-between px-3 py-2 bg-muted/30">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleExpand(d.id)}
                                className="font-mono text-sm font-semibold hover:underline"
                                data-testid={`expand-domain-${d.id}`}
                              >
                                {d.name}
                              </button>
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${statusColor}`}>
                                {status || "pending"}
                              </span>
                              <span className="text-[10px] text-muted-foreground">{d.region}</span>
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => handleVerifyDomain(d.id)}
                                disabled={verifyingDomain === d.id}
                                className="text-[10px] font-semibold px-2 py-1 bg-[#002FA7] text-white rounded-sm hover:bg-[#002280] disabled:opacity-50"
                                data-testid={`verify-domain-${d.id}`}
                              >
                                {verifyingDomain === d.id ? "..." : "Verifieer"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteDomain(d.id, d.name)}
                                className="text-[10px] font-semibold px-2 py-1 bg-red-50 text-red-700 rounded-sm hover:bg-red-100"
                                data-testid={`delete-domain-${d.id}`}
                              >
                                Verwijder
                              </button>
                            </div>
                          </div>
                          {isOpen && (
                            <div className="p-3 bg-white">
                              {records.length === 0 ? (
                                <div className="text-xs text-muted-foreground">Records laden...</div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-[11px] border-collapse">
                                    <thead>
                                      <tr className="bg-muted/50">
                                        <th className="text-left px-2 py-1.5 font-semibold">Type</th>
                                        <th className="text-left px-2 py-1.5 font-semibold">Naam / Host</th>
                                        <th className="text-left px-2 py-1.5 font-semibold">Waarde</th>
                                        <th className="text-left px-2 py-1.5 font-semibold">TTL / Prio</th>
                                        <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {records.map((r, i) => (
                                        <tr key={i} className="border-b border-border/50" data-testid={`dns-record-${i}`}>
                                          <td className="px-2 py-1.5 font-mono font-bold">{r.type || r.record}</td>
                                          <td className="px-2 py-1.5 font-mono break-all">{r.name}</td>
                                          <td className="px-2 py-1.5 font-mono break-all max-w-[280px]">{r.value}</td>
                                          <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.ttl || "Auto"}{r.priority !== undefined ? ` / ${r.priority}` : ""}</td>
                                          <td className="px-2 py-1.5">
                                            <span className={`text-[9px] uppercase tracking-wider font-bold ${r.status === "verified" ? "text-green-700" : "text-amber-700"}`}>
                                              {r.status || "-"}
                                            </span>
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <button
                                              type="button"
                                              onClick={() => copyToClipboard(r.value, `${r.type || r.record} waarde gekopieerd`)}
                                              className="text-[10px] text-[#002FA7] hover:underline"
                                              data-testid={`copy-dns-${i}`}
                                            >
                                              Kopieer
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <p className="text-[10px] text-muted-foreground mt-3">
                                    Voeg deze records toe bij je DNS-provider. DNS-propagatie duurt meestal 15 min tot 48 uur.
                                    Na toevoegen klik op <b>Verifieer</b>. Zodra status <b>verified</b> is kun je "noreply@{d.name}" als afzender gebruiken.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <Separator />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 bg-[#EAF1FF] border border-[#002FA7]/20 rounded-sm px-3 py-2">
                  <p className="text-[11px] text-[#002FA7] font-semibold" data-testid="email-firma-scope-note">
                    Deze instellingen gelden alleen voor de firma die je nu geopend hebt. Elke firma kan dus een eigen dagrapport-uur en eigen te-laat-meldingen hebben.
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Dagrapport Uur</Label>
                  <Select value={rapportUur} onValueChange={setRapportUur}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="rapport-uur-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Te laat vanaf</Label>
                  <Input
                    type="time"
                    value={teLaatVanaf}
                    onChange={(e) => setTeLaatVanaf(e.target.value)}
                    className="rounded-sm mt-1"
                    data-testid="te-laat-vanaf-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Wie later inklokt dan dit uur staat als "te laat" (Belgische tijd)</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Werkdag begint om</Label>
                  <Input
                    type="time"
                    value={werkdagStart}
                    onChange={(e) => setWerkdagStart(e.target.value)}
                    className="rounded-sm mt-1"
                    data-testid="werkdag-start-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Uren worden vanaf dit uur geteld. Laat leeg om altijd de exacte inkloktijd te gebruiken.
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Werkdag eindigt om</Label>
                  <Input
                    type="time"
                    value={werkdagEind}
                    onChange={(e) => setWerkdagEind(e.target.value)}
                    className="rounded-sm mt-1"
                    data-testid="werkdag-eind-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Einduur van de werkdag. De herinnering "vergeten uitklokken" vertrekt van dit uur.
                  </p>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Tolerantie vroeger inklokken (min)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="240"
                    value={startTolerantie}
                    onChange={(e) => setStartTolerantie(e.target.value)}
                    className="rounded-sm mt-1"
                    data-testid="start-tolerantie-input"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Klokt iemand tot zoveel minuten te vroeg in, dan tellen de uren pas vanaf het startuur
                    (bv. 06:50 wordt 07:00). Klokt hij vroeger dan dat, dan telt zijn eigen inkloktijd.
                  </p>
                </div>
                <div className="sm:col-span-2 border border-border rounded-sm p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">Weekrooster</Label>
                      <p className="text-[10px] text-muted-foreground">
                        Afwijkende openings- en sluitingsuren per dag (bv. zaterdag korter, zondag gesloten).
                        Laat een dag leeg om de uren hierboven te gebruiken.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-sm h-8 text-xs shrink-0"
                      onClick={() => kopieerDagNaarAlle("ma", true)}
                      data-testid="weekrooster-copy-werkdagen"
                    >
                      <Copy size={13} weight="bold" className="mr-1" />
                      Ma → ma–vr
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    {DAGEN.map((d) => {
                      const rij = weekrooster[d.k] || {};
                      const zet = (veld, waarde) =>
                        setWeekrooster((w) => ({ ...w, [d.k]: { ...(w[d.k] || {}), [veld]: waarde } }));
                      return (
                        <div key={d.k} className="flex items-center gap-2" data-testid={`weekrooster-${d.k}`}>
                          <span className="w-20 text-xs font-bold shrink-0">{d.l}</span>
                          <Input
                            type="time"
                            value={rij.start || ""}
                            onChange={(e) => zet("start", e.target.value)}
                            disabled={rij.gesloten}
                            className="rounded-sm h-9 w-[115px]"
                            data-testid={`weekrooster-${d.k}-start`}
                          />
                          <span className="text-xs text-muted-foreground">tot</span>
                          <Input
                            type="time"
                            value={rij.eind || ""}
                            onChange={(e) => zet("eind", e.target.value)}
                            disabled={rij.gesloten}
                            className="rounded-sm h-9 w-[115px]"
                            data-testid={`weekrooster-${d.k}-eind`}
                          />
                          <label className="flex items-center gap-1.5 ml-auto shrink-0">
                            <Switch
                              checked={!!rij.gesloten}
                              onCheckedChange={(v) => zet("gesloten", v)}
                              data-testid={`weekrooster-${d.k}-gesloten`}
                            />
                            <span className="text-[11px] font-semibold text-muted-foreground">Gesloten</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => kopieerDagNaarAlle(d.k)}
                            title={`Kopieer ${d.l} naar alle dagen`}
                            className="w-8 h-8 flex items-center justify-center text-[#002FA7] border border-[#002FA7]/30 rounded-sm active:bg-[#EAF1FF] shrink-0"
                            data-testid={`weekrooster-${d.k}-copy-all`}
                          >
                            <Copy size={13} weight="bold" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:pt-5 sm:col-span-2">
                  <Switch checked={teLaatEmail} onCheckedChange={setTeLaatEmail} data-testid="te-laat-email-switch" />
                  <div>
                    <Label className="text-xs font-semibold">Te laat meldingen</Label>
                    <p className="text-[10px] text-muted-foreground">Direct email bij te laat inklokken</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:pt-2 sm:col-span-2">
                  <Switch checked={uitklokHerinnering} onCheckedChange={setUitklokHerinnering} data-testid="uitklok-herinnering-switch" />
                  <div>
                    <Label className="text-xs font-semibold">Herinnering vergeten uitklokken</Label>
                    <p className="text-[10px] text-muted-foreground">
                      Melding + email met wie nog ingeklokt staat
                    </p>
                  </div>
                </div>
                {uitklokHerinnering && (
                  <div className="sm:col-span-2 bg-[#EAF1FF] border border-[#002FA7]/20 rounded-sm p-3">
                    <Label className="text-xs font-semibold">Hoeveel minuten na het einduur van de werkdag?</Label>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Input
                        type="number"
                        min="5"
                        max="300"
                        value={uitklokMin}
                        onChange={(e) => setUitklokMin(e.target.value)}
                        className="rounded-sm w-28"
                        data-testid="uitklok-herinnering-min"
                      />
                      <span className="text-xs font-semibold text-muted-foreground" data-testid="uitklok-herinnering-uur">
                        minuten na {werkdagEind || "18:00"} → dus om{" "}
                        {(() => {
                          const [u, m] = (werkdagEind || "18:00").split(":").map((x) => parseInt(x) || 0);
                          const t = (u * 60 + m + (parseInt(uitklokMin) || 0)) % 1440;
                          return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
                        })()}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-sm h-9 ml-auto"
                        disabled={herinneringBezig}
                        onClick={async () => {
                          setHerinneringBezig(true);
                          try {
                            const { data } = await axios.post(`${API}/aanwezigheid/herinnering-nu`);
                            if (data.aantal) toast.success(`${data.aantal} medewerker(s) staan nog ingeklokt — melding verstuurd`);
                            else toast.success(data.reden || "Iedereen is uitgeklokt");
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Versturen mislukt");
                          } finally { setHerinneringBezig(false); }
                        }}
                        data-testid="uitklok-herinnering-test-btn"
                      >
                        {herinneringBezig ? "Bezig..." : "Nu controleren"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex gap-2 flex-wrap">
                <Button onClick={handleSaveEmail} disabled={savingEmail} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="save-email-settings-btn">
                  {savingEmail ? "Opslaan..." : "Opslaan"}
                </Button>
                <Button onClick={handleTestEmail} disabled={sendingTest} variant="outline" className="rounded-sm" data-testid="test-email-btn">
                  <EnvelopeSimple size={14} className="mr-1.5" />
                  {sendingTest ? "Versturen..." : "Test Email"}
                </Button>
                <Button onClick={handleSendDagrapport} disabled={sendingDag} variant="outline" className="rounded-sm" data-testid="send-dagrapport-btn">
                  <PaperPlaneTilt size={14} className="mr-1.5" />
                  {sendingDag ? "Versturen..." : "Dagrapport Nu"}
                </Button>
              </div>
            </div>
          </div>

          {/* ═══ TELEGRAM ═══ */}
          <div className="bg-white border border-border rounded-sm p-6" data-testid="telegram-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-[#229ED9]/10 flex items-center justify-center">
                <TelegramLogo size={18} weight="bold" className="text-[#229ED9]" />
              </div>
              <div>
                <h3 className="font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>Telegram meldingen</h3>
                <p className="text-[10px] text-muted-foreground">Gratis push-berichten op je telefoon</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-[11px] font-semibold text-muted-foreground">
                  {tg.actief ? "Aan" : "Uit"}
                </span>
                <Switch
                  checked={tg.actief}
                  onCheckedChange={(v) => setTgVeld("actief", v)}
                  data-testid="telegram-actief-switch"
                />
              </div>
            </div>

            <div className="bg-[#EAF1FF] border border-[#002FA7]/20 rounded-sm p-3 mb-4 text-[11px] leading-relaxed">
              <p className="font-bold text-[#002FA7] mb-1">Zo zet je het op (2 minuten):</p>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Open Telegram en zoek <b>@BotFather</b>.</li>
                <li>Stuur <b>/newbot</b>, kies een naam en een gebruikersnaam die eindigt op <b>bot</b>.</li>
                <li>BotFather geeft je een <b>token</b> (bv. <code>123456:AAE...</code>) — plak die hieronder.</li>
                <li>Zoek je nieuwe bot in Telegram en stuur hem <b>/start</b> (of voeg hem toe aan een groep en stuur daar een bericht).</li>
                <li>Klik hieronder op <b>Chat ophalen</b> en daarna op <b>Testbericht</b>.</li>
              </ol>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Bot token</Label>
                <Input
                  value={tg.bot_token}
                  onChange={(e) => setTgVeld("bot_token", e.target.value)}
                  placeholder="123456789:AAExxxxxxxxxxxxxxxxxxxxxxxx"
                  className="rounded-sm mt-1 font-mono text-xs"
                  data-testid="telegram-token-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Chat ID</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={tg.chat_id}
                    onChange={(e) => setTgVeld("chat_id", e.target.value)}
                    placeholder="bv. 123456789"
                    className="rounded-sm font-mono text-xs"
                    data-testid="telegram-chatid-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm shrink-0"
                    disabled={tgZoeken || !tg.bot_token}
                    onClick={handleTelegramChatId}
                    data-testid="telegram-chatid-btn"
                  >
                    {tgZoeken ? "Zoeken..." : "Chat ophalen"}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Alle meldingen gaan naar deze ene chat (persoonlijk of groep).
                </p>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-3">
              {[
                { k: "te_laat", l: "Te laat ingeklokt", d: "Direct bericht zodra iemand te laat inklokt" },
                { k: "uitklok", l: "Vergeten uitklokken", d: "Lijst na sluitingstijd van wie nog ingeklokt staat" },
                { k: "dagrapport", l: "Dagelijks overzicht", d: `Elke dag rond ${rapportUur}:00 een samenvatting` },
                { k: "ritten", l: "Ritten wagenpark", d: "Bericht bij start en stop van een rit, met bestuurder en voertuig" },
                { k: "rit_open", l: "Rit blijft open", d: `Waarschuwing als een rit langer dan ${tg.rit_open_uren || 8} uur open staat` },
                { k: "blokkade", l: "Account geblokkeerd", d: "Bericht zodra iemand na 5 foute wachtwoorden geblokkeerd raakt" },
                { k: "nieuwe_medewerker", l: "Nieuwe medewerker", d: "Bericht zodra een beheerder een medewerker toevoegt" },
              ].map((r) => (
                <div key={r.k} className="flex items-center gap-3">
                  <Switch
                    checked={!!tg[r.k]}
                    onCheckedChange={(v) => setTgVeld(r.k, v)}
                    disabled={!tg.actief}
                    data-testid={`telegram-${r.k.replace("_", "-")}-switch`}
                  />
                  <div>
                    <Label className="text-xs font-semibold">{r.l}</Label>
                    <p className="text-[10px] text-muted-foreground">{r.d}</p>
                  </div>
                </div>
              ))}
            </div>

            {tg.rit_open && (
              <div className="mt-3 flex items-center gap-2 bg-[#EAF1FF] border border-[#002FA7]/20 rounded-sm p-2.5">
                <Label className="text-xs font-semibold">Waarschuwen na</Label>
                <Input
                  type="number"
                  min="1"
                  max="24"
                  value={tg.rit_open_uren}
                  onChange={(e) => setTgVeld("rit_open_uren", parseInt(e.target.value, 10) || 1)}
                  className="rounded-sm h-8 w-20 text-center"
                  data-testid="telegram-rit-uren-input"
                />
                <span className="text-xs font-semibold text-muted-foreground">uur open staan</span>
              </div>
            )}

            <Separator className="my-4" />

            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleSaveTelegram}
                disabled={tgSaving}
                className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
                data-testid="save-telegram-btn"
              >
                {tgSaving ? "Opslaan..." : "Opslaan"}
              </Button>
              <Button
                onClick={handleTelegramTest}
                disabled={tgTesten || !tg.bot_token || !tg.chat_id}
                variant="outline"
                className="rounded-sm"
                data-testid="telegram-test-btn"
              >
                <TelegramLogo size={14} className="mr-1.5" />
                {tgTesten ? "Versturen..." : "Opslaan + testbericht"}
              </Button>
              <Button
                onClick={handleTestHerinnering}
                disabled={tgHerinnering}
                variant="outline"
                className="rounded-sm"
                data-testid="telegram-test-uitklok-btn"
              >
                {tgHerinnering ? "Versturen..." : "Test 'vergeten uitklokken' nu"}
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Elk bericht begint met de firmanaam tussen haakjes. Instellingen
              gelden per firma: wissel bovenaan van firma om een andere bot in te stellen.
            </p>
          </div>
        </TabsContent>

        {/* ═══ BEHEER TAB ═══ */}
        <TabsContent value="beheer" className="mt-5 space-y-5">
          {/* Filiaal Verwijderen */}
          <div className="bg-white border border-border rounded-sm p-6" data-testid="delete-filiaal-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-[#E53E3E]/10 flex items-center justify-center">
                <Buildings size={18} weight="bold" className="text-[#E53E3E]" />
              </div>
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Filiaal Verwijderen</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Filiaal</Label>
                <Select value={selectedFiliaal} onValueChange={setSelectedFiliaal}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="delete-filiaal-select"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>{filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam} {f.stad ? `(${f.stad})` : ""}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Admin Wachtwoord</Label>
                <Input type="password" value={filiaalWachtwoord} onChange={(e) => setFiliaalWachtwoord(e.target.value)} placeholder="Wachtwoord" className="rounded-sm mt-1" data-testid="filiaal-delete-password" />
              </div>
            </div>
            <Button onClick={handleFiliaalDeleteConfirm} variant="destructive" className="rounded-sm mt-4" data-testid="delete-filiaal-btn">
              <Trash size={14} weight="bold" className="mr-1.5" /> Verwijderen
            </Button>
          </div>

          {/* Medewerker Verwijderen */}
          <div className="bg-white border border-border rounded-sm p-6" data-testid="delete-medewerker-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-[#E53E3E]/10 flex items-center justify-center">
                <Users size={18} weight="bold" className="text-[#E53E3E]" />
              </div>
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Medewerker Verwijderen</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Medewerker</Label>
                <Select value={selectedMedewerker} onValueChange={setSelectedMedewerker}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="delete-medewerker-select"><SelectValue placeholder="Selecteer..." /></SelectTrigger>
                  <SelectContent>{medewerkers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Admin Wachtwoord</Label>
                <Input type="password" value={medWachtwoord} onChange={(e) => setMedWachtwoord(e.target.value)} placeholder="Wachtwoord" className="rounded-sm mt-1" data-testid="medewerker-delete-password" />
              </div>
            </div>
            <Button onClick={handleMedDeleteConfirm} variant="destructive" className="rounded-sm mt-4" data-testid="delete-medewerker-btn">
              <Trash size={14} weight="bold" className="mr-1.5" /> Verwijderen
            </Button>
          </div>

          {/* Maandgegevens Verwijderen */}
          <div className="bg-white border border-border rounded-sm p-6" data-testid="delete-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-[#E53E3E]/10 flex items-center justify-center">
                <Trash size={18} weight="bold" className="text-[#E53E3E]" />
              </div>
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Maandgegevens Verwijderen</h3>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold">Maand</Label>
                <Select value={deleteMaand} onValueChange={setDeleteMaand}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="delete-maand-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MAAND_NAMEN.map((naam, idx) => (<SelectItem key={idx} value={String(idx + 1)}>{naam}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Jaar</Label>
                <Select value={deleteJaar} onValueChange={setDeleteJaar}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="delete-jaar-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2024, 2025, 2026, 2027].map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Wachtwoord</Label>
                <Input type="password" value={deleteWachtwoord} onChange={(e) => setDeleteWachtwoord(e.target.value)} placeholder="Admin ww" className="rounded-sm mt-1" data-testid="delete-password-input" />
              </div>
            </div>
            <Button onClick={handleDeleteConfirm} variant="destructive" className="rounded-sm mt-4" data-testid="delete-month-btn">
              <Trash size={14} weight="bold" className="mr-1.5" /> Verwijderen
            </Button>
            {deleteResult && (
              <div className="bg-muted rounded-sm p-3 text-sm mt-3" data-testid="delete-result">
                <p className="font-medium">{deleteResult.message}</p>
                <p className="text-muted-foreground text-xs mt-1">{deleteResult.aanwezigheid_verwijderd} records, {deleteResult.meldingen_verwijderd} meldingen</p>
              </div>
            )}
          </div>

          {/* Aanwezigheid Log Verwijderen */}
          <div className="bg-white border border-border rounded-sm p-6" data-testid="delete-aanwezigheid-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-[#E53E3E]/10 flex items-center justify-center">
                <Clock size={18} weight="bold" className="text-[#E53E3E]" />
              </div>
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Aanwezigheid Log Verwijderen</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Verwijder ALLE aanwezigheidsregistraties (inklok/uitklok gegevens)</p>
            <div className="max-w-xs">
              <Label className="text-xs font-semibold">Admin Wachtwoord</Label>
              <Input type="password" value={aanwLogWw} onChange={(e) => setAanwLogWw(e.target.value)} placeholder="Wachtwoord" className="rounded-sm mt-1" data-testid="aanw-delete-password" />
            </div>
            <Button onClick={handleAanwDeleteConfirm} variant="destructive" className="rounded-sm mt-4" data-testid="delete-aanwezigheid-btn">
              <Trash size={14} weight="bold" className="mr-1.5" /> Alle Logs Verwijderen
            </Button>
            {aanwResult && (
              <div className="bg-muted rounded-sm p-3 text-sm mt-3" data-testid="aanw-delete-result">
                <p className="font-medium">{aanwResult.message}</p>
                <p className="text-muted-foreground text-xs mt-1">{aanwResult.verwijderd} records verwijderd</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ═══ BEVEILIGING TAB ═══ */}
        <TabsContent value="beveiliging" className="mt-5 space-y-5">
          <SessieOverzicht isAdmin={isAdmin} />

          <div className="bg-white border border-border rounded-sm p-6" data-testid="sessie-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                <Clock size={18} weight="bold" className="text-[#002FA7]" />
              </div>
              <div>
                <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Automatisch Uitloggen</h3>
                <p className="text-xs text-muted-foreground">Logt webgebruikers uit na een periode zonder activiteit</p>
              </div>
            </div>
            <div className="flex items-center justify-between py-3 border-t border-border">
              <div>
                <p className="text-sm font-semibold">Automatisch uitloggen aan</p>
                <p className="text-xs text-muted-foreground">Zet uit om sessies open te laten staan</p>
              </div>
              <Switch checked={autoLogoutAan} onCheckedChange={setAutoLogoutAan} data-testid="auto-logout-switch" />
            </div>
            <div className="py-3 border-t border-border">
              <Label className="text-xs font-semibold">Inactiviteit (minuten)</Label>
              <Input
                type="number"
                min="1"
                max="480"
                value={autoLogoutMin}
                onChange={(e) => setAutoLogoutMin(e.target.value)}
                disabled={!autoLogoutAan}
                className="rounded-sm mt-1 w-full sm:w-40"
                data-testid="auto-logout-minuten-input"
              />
              <p className="text-xs text-muted-foreground mt-1.5">Tussen 1 en 480 minuten. Standaard 30.</p>
            </div>
            {isSuperadmin && (
              <div className="py-3 border-t border-border" data-testid="loon-minuten-sectie">
                <Label className="text-xs font-semibold">Loongegevens zichtbaar (minuten)</Label>
                <Input
                  type="number"
                  min="1"
                  max="480"
                  value={loonMin}
                  onChange={(e) => setLoonMin(e.target.value)}
                  className="rounded-sm mt-1 w-full sm:w-40"
                  data-testid="loon-minuten-input"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Na elke aanmelding staan uurlonen en loonkosten op slot. Wie het recht heeft, geeft zijn
                  wachtwoord opnieuw en ziet ze dan zoveel minuten. Enkel de superadmin kan dit wijzigen.
                </p>
              </div>
            )}
            <Button onClick={handleSaveSessie} disabled={savingSessie} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white mt-2" data-testid="save-sessie-btn">
              <ShieldCheck size={14} weight="bold" className="mr-1.5" /> {savingSessie ? "Opslaan..." : "Opslaan"}
            </Button>
          </div>

          <div className="bg-white border border-border rounded-sm p-6" data-testid="mijn-account-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                <EnvelopeSimple size={18} weight="bold" className="text-[#002FA7]" />
              </div>
              <div>
                <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Mijn Loginadres Wijzigen</h3>
                <p className="text-[11px] text-muted-foreground">Huidig: <strong data-testid="huidig-login-email">{auth?.email || "-"}</strong></p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Nieuw e-mailadres</Label>
                <Input type="email" value={nieuwLoginEmail} onChange={(e) => setNieuwLoginEmail(e.target.value)} placeholder="naam@bedrijf.be" className="rounded-sm mt-1" data-testid="nieuw-login-email-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Huidig wachtwoord (ter bevestiging)</Label>
                <Input type="password" value={emailWw} onChange={(e) => setEmailWw(e.target.value)} placeholder="Wachtwoord" className="rounded-sm mt-1" data-testid="email-wijzig-wachtwoord-input" />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Vanaf de volgende keer meld je je aan met het nieuwe adres. Je blijft nu ingelogd.</p>
            <Button onClick={handleChangeEmail} disabled={changingEmail} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white mt-4" data-testid="change-email-btn">
              <EnvelopeSimple size={14} weight="bold" className="mr-1.5" /> {changingEmail ? "Wijzigen..." : "E-mailadres Wijzigen"}
            </Button>
          </div>

          <div className="bg-white border border-border rounded-sm p-6" data-testid="password-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                <LockKey size={18} weight="bold" className="text-[#002FA7]" />
              </div>
              <h3 className="font-bold text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Admin Wachtwoord Wijzigen</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Huidig Wachtwoord</Label>
                <Input type="password" value={huidigWw} onChange={(e) => setHuidigWw(e.target.value)} placeholder="Huidig wachtwoord" className="rounded-sm mt-1" data-testid="current-password-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Nieuw Wachtwoord</Label>
                <Input type="password" value={nieuwWw} onChange={(e) => setNieuwWw(e.target.value)} placeholder="Min. 4 tekens" className="rounded-sm mt-1" data-testid="new-password-input" />
              </div>
            </div>
            <Button onClick={handleChangePassword} disabled={changingWw} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white mt-4" data-testid="change-password-btn">
              <LockKey size={14} weight="bold" className="mr-1.5" /> {changingWw ? "Wijzigen..." : "Wachtwoord Wijzigen"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Confirm Dialogs */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Bevestig Verwijdering</DialogTitle>
            <DialogDescription>Alle gegevens voor <strong>{MAAND_NAMEN[parseInt(deleteMaand) - 1]} {deleteJaar}</strong> worden permanent verwijderd.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-[#E53E3E]/5 rounded-sm border border-[#E53E3E]/20">
            <Warning size={18} className="text-[#E53E3E] shrink-0" />
            <p className="text-xs text-[#E53E3E] font-medium">Dit kan niet ongedaan worden gemaakt.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-sm" data-testid="confirm-delete-month-btn">{deleting ? "Verwijderen..." : "Verwijderen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={filiaalConfirmOpen} onOpenChange={setFiliaalConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="confirm-delete-filiaal-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Filiaal Verwijderen</DialogTitle>
            <DialogDescription><strong>{selectedFilNaam}</strong> wordt permanent verwijderd. Medewerkers blijven bestaan.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-[#E53E3E]/5 rounded-sm border border-[#E53E3E]/20">
            <Warning size={18} className="text-[#E53E3E] shrink-0" />
            <p className="text-xs text-[#E53E3E] font-medium">Dit kan niet ongedaan worden gemaakt.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFiliaalConfirmOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button variant="destructive" onClick={handleFiliaalDelete} disabled={deletingFiliaal} className="rounded-sm" data-testid="confirm-delete-filiaal-btn">{deletingFiliaal ? "Verwijderen..." : "Verwijderen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Aanwezigheid Dialog */}
      <Dialog open={aanwConfirmOpen} onOpenChange={setAanwConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="confirm-delete-aanw-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Alle Aanwezigheid Verwijderen</DialogTitle>
            <DialogDescription>ALLE inklok/uitklok registraties worden permanent verwijderd.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-[#E53E3E]/5 rounded-sm border border-[#E53E3E]/20">
            <Warning size={18} className="text-[#E53E3E] shrink-0" />
            <p className="text-xs text-[#E53E3E] font-medium">Dit kan niet ongedaan worden gemaakt. Alle logs zijn definitief weg.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAanwConfirmOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button variant="destructive" onClick={handleAanwDelete} disabled={deletingAanw} className="rounded-sm" data-testid="confirm-delete-aanw-btn">{deletingAanw ? "Verwijderen..." : "Definitief Verwijderen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Medewerker Dialog */}
      <Dialog open={medConfirmOpen} onOpenChange={setMedConfirmOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="confirm-delete-medewerker-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Medewerker Verwijderen</DialogTitle>
            <DialogDescription><strong>{selectedMedNaam}</strong> wordt permanent verwijderd.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 p-3 bg-[#E53E3E]/5 rounded-sm border border-[#E53E3E]/20">
            <Warning size={18} className="text-[#E53E3E] shrink-0" />
            <p className="text-xs text-[#E53E3E] font-medium">Dit kan niet ongedaan worden gemaakt.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMedConfirmOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button variant="destructive" onClick={handleMedDelete} disabled={deletingMed} className="rounded-sm" data-testid="confirm-delete-medewerker-btn">{deletingMed ? "Verwijderen..." : "Definitief Verwijderen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
